// ── Raids ───────────────────────────────────────────────────────────────

import type { RoomAccent, GroupPosition, PlayerTag } from './board'
import type { PlayerRole } from '@/types/weapon'

/**
 * Nominal alias for a raid row's id, matching the `RoomId` / `GroupId` /
 * `PlayerId` pattern. The id is the backend DB PK (`i64`) stringified for
 * the frontend convention. Cross-references (`RaidRoom.raidId`) use this
 * alias so the type system catches accidental id swaps at compile time.
 */
export type RaidId = string
export type RaidDurationMinutes = number

export const RaidStatus = {
  Scheduled: 'scheduled',
  InProgress: 'in_progress',
  Completed: 'completed',
  Cancelled: 'cancelled',
  Overdue: 'overdue',
} as const

export type RaidStatus = (typeof RaidStatus)[keyof typeof RaidStatus]

export interface RaidCalendarDay {
  readonly date: Date
  readonly raids: ReadonlyArray<RaidSummary>
}

export interface RaidCalendarWeek {
  readonly startsOn: Date
  readonly days: ReadonlyArray<RaidCalendarDay>
}

/**
 * The summary record the backend exposes via `Raids(guildId)` (one per
 * raid in the guild, ordered by `scheduled_starts_at` then `name`).
 */
export interface RaidSummary {
  readonly id: RaidId
  readonly guildId: string
  readonly name: string
  /** Whether officers have published this raid. */
  readonly isPublished: boolean
  /** RFC 3339 timestamp string (server emits `.toString()`-able chrono values). */
  readonly scheduledStartsAt: string | null
  readonly durationMinutes: RaidDurationMinutes | null
  readonly cancelledAt: string | null
  readonly completedAt: string | null
  /** `id` of the raid this one was cloned from, or null if created from scratch. */
  readonly sourceRaidId: RaidId | null
  /**
   * Plan 06 — recurring-series metadata. `Some(info)` when this raid is
   * one of N rows sharing a `recurrenceSeriesId`; `null` for one-off
   * scheduled raids. The calendar renders a small badge
   * (e.g. `↻ 3 / 8`) when this is non-null; the parent (`index = 0`) is
   * the only row the planner's "edit" affordance should open.
   */
  readonly recurrence: RecurrenceInfo | null
  readonly notes: string
  /** Discord snowflake of the officer who created the raid, or null. */
  readonly createdBy: string | null
  readonly createdAt: string
  readonly updatedAt: string
}

/**
 * Plan 06 — per-series metadata exposed on `RaidSummary.recurrence`. The
 * `seriesId` is the parent's `raids.id`; the calendar uses it to group
 * chips that share a series. `parentId` mirrors `seriesId` for now but
 * is named so a future "fork a child as the new parent" affordance
 * doesn't have to bump the wire contract. `total` is a denormalised
 * instance count so the calendar can render "3 of 8" badges without a
 * second round-trip. `frequency` is the cadence string
 * (`'weekly'` / `'biweekly'`); the api derives it from the spacing
 * between the first two instances in the series.
 */
export interface RecurrenceInfo {
  readonly seriesId: RaidId
  readonly parentId: RaidId
  readonly index: number
  readonly total: number
  readonly frequency: string
}

/**
 * Plan 06 — `hasRecurrence(raid)` is the canonical predicate the calendar
 * chips use to decide whether to render the recurrence badge. A raid is
 * "recurring" iff `recurrence !== null`. The predicate lives next to
 * `RecurrenceInfo` so the calendar agent can import it without re-deriving
 * the boolean from the field shape (keeps the contract stable if a future
 * migration adds e.g. a separate "exception" flag).
 */
export function hasRecurrence(raid: RaidSummary): boolean {
  return raid.recurrence !== null
}

/**
 * A raid-scoped room — the live-board `Room` mirrored into the raid
 * snapshot. The `stableId` is preserved so Pool-invariant checks on the
 * client side still work (`'pool'`, `'A1'`, `'P2'`, …).
 */
export interface RaidRoom {
  readonly id: RaidId
  readonly stableId: string
  readonly guildId: string
  readonly raidId: RaidId
  /**
   * Plan 04 — nullable FK to `raid_stages.id`. `null` for cross-stage rows
   * (Pool today) that should render on every stage of the active raid.
   * On the raid board, the planner's stage tab strip filters rooms /
   * groups / players / builds via this column.
   */
  readonly stageId: string | null
  readonly name: string
  readonly accent: RoomAccent
  readonly noPlayerLimit: boolean
  readonly orderIndex: number
}

/**
 * A raid-scoped group — mirrors `PlayerGroup` with the additional `raidId`
 * for snapshot scoping. Same stable-id convention as rooms.
 */
export interface RaidGroup {
  readonly id: RaidId
  readonly stableId: string
  readonly guildId: string
  readonly raidId: RaidId
  /** Plan 04 — nullable stage id (see `RaidRoom.stageId`). */
  readonly stageId: string | null
  /** `0` sentinel when the live `groups.id` was deleted (FK SET NULL). */
  readonly roomId: number
  readonly label: string
  readonly position: GroupPosition
  readonly maxPlayers: number
  readonly collapsed: boolean
  readonly orderIndex: number
  readonly playerIds: ReadonlyArray<RaidPlayerId>
}

/**
 * Nominal alias for a raid-snapshotted player's id. Distinct from
 * `PlayerId` (which references live `players.id`); a raid player's id is
 * the `raid_players.id` PK, not the live row.
 */
export type RaidPlayerId = string

/**
 * A raid-snapshotted player. Weapon loadout and tag assignments are both
 * snapshot-scoped; the tag catalog itself remains shared by the guild.
 */
export interface RaidPlayer {
  readonly id: RaidPlayerId
  readonly guildId: string
  readonly raidId: RaidId
  /** Plan 04 — nullable stage id (see `RaidRoom.stageId`). */
  readonly stageId: string | null
  /** `null` when the live `players.id` was deleted (FK SET NULL). */
  readonly playerId: string | null
  readonly discordUserId: string
  /** True once this snapshot player has signed up for this raid. */
  readonly hasSignedIn: boolean
  readonly discordName: string
  readonly guildNickname: string
  readonly playerNickname: string
  readonly playerAccountName: string
  readonly inGameName: string
  readonly role: PlayerRole
  /** `0` sentinel when the live `groups.id` was deleted (FK SET NULL). */
  readonly groupId: number
  readonly groupOrderIndex: number
  readonly mainWeapons: ReadonlyArray<string>
  readonly altWeapons: ReadonlyArray<string>
  readonly activeBuildIndex: number
  readonly builds: ReadonlyArray<ReadonlyArray<string>>
  readonly tags: ReadonlyArray<PlayerTag>
}

/**
 * A single saved-build row from a raid snapshot. Mirrors `player_builds`
 * but scoped to the raid.
 */
export interface RaidPlayerBuild {
  readonly id: RaidId
  readonly playerId: string | null
  readonly raidId: RaidId
  /** Plan 04 — nullable stage id (see `RaidRoom.stageId`). */
  readonly stageId: string | null
  readonly orderIndex: number
  readonly weaponSlugs: ReadonlyArray<string>
}

/**
 * Composite raid snapshot returned by `raidBoard(guildId, raidId)`. The
 * store caches one of these per `activeRaidId` and renders it through
 * the same `RoomSection` / `PlayerGroup` / `PlayerCard` components used
 * for the live board, with `mode: 'raid'` suppressing every mutation
 * affordance.
 */
export interface RaidBoardPayload {
  readonly raid: RaidSummary
  readonly rooms: ReadonlyArray<RaidRoom>
  readonly groups: ReadonlyArray<RaidGroup>
  readonly players: ReadonlyArray<RaidPlayer>
}

/**
 * Which board the store should render. `live` reads from the
 * `players`/`groups`/`rooms` refs (the `board(guildId)` query). `raid`
 * reads from `activeRaidBoard`. The mode flows down through
 * `RoomSection` / `PlayerGroup` / `PlayerCard` so the chip strip, DnD
 * affordances, and per-room rename / reorder buttons know whether to
 * hide themselves (raid snapshots are read-only at the UI layer — clones
 * + apply-to-board flow through the raid selector strip).
 */
export const BoardMode = {
  Live: 'live',
  Raid: 'raid',
} as const

export type BoardMode = (typeof BoardMode)[keyof typeof BoardMode]

/**
 * Per-guild caps. Mirrors the backend `invariants::MAX_RAIDS_PER_GUILD`
 * / `MAX_NAME_LEN`. The pre-flight checks in
 * the store use these so a user submitting an over-cap mutation gets a
 * clean error before the network round-trip.
 *
 * `MAX_NAME_LEN` is shared between officer-managed raid names — kept here
 * next to the other raid caps because the bar is
 * raid-shaped (single Guild has at most 100 raids × ≤80-char name, plus
 * raids), but the validation routines for both sides consult it.
 */
export const MAX_RAIDS_PER_GUILD = 100
export const MAX_NAME_LEN = 80


